import { inject, Injectable } from '@angular/core';
import { MatrixService } from '../matrix/matrix.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { MatrixEvent } from 'matrix-js-sdk';
import { Settings } from '../settings/settings.service';
import { FileService } from '../file/file.service';
import { RoomListAccessService } from '../room-list/room-list-access.service';

@Injectable({
  providedIn: 'root'
})
/**
 * Service zur Verwaltung der UserIcons
 *
 * Begründung: Helfer Service, daher keine AFO
 */
export class UserService {
  userIconCacheData = new Map<string, string>();
  userIconInProcess = new Map<string, boolean>();
  userIconInitialLoad = new Map<string, boolean>();

  private readonly clientService = inject(MatrixService);
  private readonly roomListAccessService = inject(RoomListAccessService);
  private readonly fileService = inject(FileService);

  private readonly _userIconCache = new BehaviorSubject<Map<string, string>>(this.userIconCacheData);
  readonly userCache: Observable<Map<string, string>> = this._userIconCache.asObservable();

  public setUserData(mxid: string, userIcon: string): void {
    this.userIconCacheData.set(mxid, userIcon);
    this.refreshUserIcons();
  }

  public async initialLoad(mxid: string): Promise<void> {
    if (!mxid) {
      console.error('no initial load of user Icon without mxid possible');
      throw new Error('no initial load of user Icon without mxid possible');
    }
    if (this.userIconInitialLoad.get(mxid)) {
      console.info('mxid already initially loaded');
      throw new Error('mxid already loaded');
    }
    this.userIconCacheData.set(mxid, '');
    return this.updateUserData(mxid);
  }

  /**
   * Läd den Raumavatar inital, wenn er vorhanden ist.
   * @param roomId
   */
  public async initialLoadRoomData(roomId: string): Promise<void> {
    if (!roomId) {
      console.error('no initial load of user Icon without roomId possible');
      throw new Error('no initial load of user Icon without roomId possible');
    }
    if (this.userIconInitialLoad.get(roomId)) {
      console.info('roomId already initially loaded');
    }
    this.userIconCacheData.set(roomId, '');
    return this.updateRoomData(roomId);
  }

  public deleteUserAvatar(): void {
    this.clientService.client.setAvatarUrl('null');
    const userId = this.clientService.client.getUserId();
    if (userId) {
      this.setUserData(userId, '');
    }
  }

  public refreshUserIcons(): void {
    this._userIconCache.next(this.userIconCacheData);
  }

  public async loadUserIconFromSettings(): Promise<void> {
    const avatarUrl = Settings.getInstance().settingsConfigurations.avatarIcon;
    if (avatarUrl) {
      await this.downloadAndStashAvatar(this.clientService.client.getUserId()!, avatarUrl);
    }
  }

  public async updateUserData(mxid: string): Promise<void> {
    if (!mxid) {
      throw new Error(`No required parameter mxid was found`);
    }
    this.userIconInProcess.set(mxid, true);
    const user = this.clientService.client.getUser(mxid);
    if (user?.avatarUrl) {
      return this.downloadAndStashAvatar(mxid, user?.avatarUrl);
    } else {
      console.info(`No Avatar Url for ${mxid}`);
      this.userIconInProcess.delete(mxid);
    }
  }

  /**
   * Updated den Raumavatar.
   * @param roomId
   */
  public async updateRoomData(roomId: string | undefined): Promise<void> {
    if (!roomId) {
      throw new Error('No roomId when calling updateRoomData');
    }
    this.userIconInProcess.set(roomId, true);
    const room = this.clientService.client.getRoom(roomId);

    if (!room) {
      console.warn(`Room ${roomId} not found in Matrix client`);
      this.userIconInProcess.delete(roomId);
      return;
    }

    const avatarEvent = room.currentState.getStateEvents('m.room.avatar', '');
    const avatarUrl = avatarEvent?.getContent()?.['url'];

    if (avatarUrl) {
      return await this.downloadAndStashAvatar(roomId, avatarUrl);
    } else {
      console.info(`No Avatar Url for ${roomId}`);
      this.setUserData(roomId, '');
      this.userIconInProcess.delete(roomId);
    }
  }

  public async memberEventHandler(event: MatrixEvent): Promise<void> {
    const sender = event.getSender();
    if (!sender) {
      console.error(`No Sender in event with id ${event.getId()}`);
      throw new Error(`No Sender in event with id ${event.getId()}`);
    }
    return this.updateUserData(sender);
  }

  public getBlobUrl(blob: Blob): string {
    return URL.createObjectURL(blob);
  }

  getUrlForFile(avatarFile: File | null, avatarUrl: string | null, roomId: string): string {
    if (avatarFile) {
      return URL.createObjectURL(avatarFile);
    }
    if (avatarUrl) {
      const avatar = this.userIconCacheData.get(roomId);
      if (!avatar) {
        throw new Error('No Avatar found in Cache when calling getUrlForFile');
      }
      return avatar;
    }
    return '';
  }

  private async downloadAndStashAvatar(mxid: string, url: string): Promise<void> {
    if (url && url !== 'null') {
      const avatarBlob = await this.downloadAvatar(url).catch(err => {
        this.userIconInProcess.delete(mxid);
        throw err;
      });
      const blobUrl = this.getBlobUrl(avatarBlob);
      this.setUserData(mxid, blobUrl);
      this.userIconInProcess.delete(mxid);
    } else {
      this.setUserData(mxid, '');
      this.userIconInProcess.delete(mxid);
    }
  }

  private async downloadAvatar(url: string): Promise<Blob> {
    return await this.fileService.downloadSource(url).catch(err => {
      console.error('Fehler beim Entschlüsseln der Datein:', err);
      return new Blob();
    });
  }
}
